import React, { createContext, useContext, useReducer, useMemo } from 'react';
import PropTypes from 'prop-types';

/**
 * Initial state for the application context
 */
const initialState = {
  // Search and location state
  search: {
    query: '',
    results: [],
    recentSearches: [], // Store recent searches
    isLoading: false,
    error: null
  },
  // User location data
  location: {
    current: null,
    isLoading: false,
    error: null
  },
  // User preferences
  preferences: {
    theme: 'light',
    distanceUnit: 'mi' // miles by default
  }
};

/**
 * Action types for the reducer
 */
export const ACTION_TYPES = {
  // Search actions
  SET_SEARCH_QUERY: 'SET_SEARCH_QUERY',
  SET_SEARCH_RESULTS: 'SET_SEARCH_RESULTS',
  ADD_RECENT_SEARCH: 'ADD_RECENT_SEARCH',
  SET_SEARCH_LOADING: 'SET_SEARCH_LOADING',
  SET_SEARCH_ERROR: 'SET_SEARCH_ERROR',
  CLEAR_SEARCH: 'CLEAR_SEARCH',
  
  // Location actions
  SET_CURRENT_LOCATION: 'SET_CURRENT_LOCATION',
  SET_LOCATION_LOADING: 'SET_LOCATION_LOADING',
  SET_LOCATION_ERROR: 'SET_LOCATION_ERROR',
  
  // Preference actions
  SET_THEME: 'SET_THEME',
  SET_DISTANCE_UNIT: 'SET_DISTANCE_UNIT'
};

/**
 * Reducer function for handling state updates
 * 
 * @param {Object} state - Current application state
 * @param {Object} action - Action to update state
 * @returns {Object} New application state
 */
function appReducer(state, action) {
  switch (action.type) {
    // Search actions
    case ACTION_TYPES.SET_SEARCH_QUERY:
      return {
        ...state,
        search: {
          ...state.search,
          query: action.payload
        }
      };
      
    case ACTION_TYPES.SET_SEARCH_RESULTS:
      return {
        ...state,
        search: {
          ...state.search,
          results: action.payload,
          isLoading: false,
          error: null
        }
      };
      
    case ACTION_TYPES.ADD_RECENT_SEARCH:
      // Avoid duplicates and limit to 5 recent searches
      const updatedSearches = [
        action.payload,
        ...state.search.recentSearches.filter(search => search !== action.payload)
      ].slice(0, 5);
      
      return {
        ...state,
        search: {
          ...state.search,
          recentSearches: updatedSearches
        }
      };
      
    case ACTION_TYPES.SET_SEARCH_LOADING:
      return {
        ...state,
        search: {
          ...state.search,
          isLoading: action.payload,
          error: null
        }
      };
      
    case ACTION_TYPES.SET_SEARCH_ERROR:
      return {
        ...state,
        search: {
          ...state.search,
          isLoading: false,
          error: action.payload
        }
      };
      
    case ACTION_TYPES.CLEAR_SEARCH:
      return {
        ...state,
        search: {
          ...state.search,
          query: '',
          results: [],
          isLoading: false,
          error: null
        }
      };
    
    // Location actions
    case ACTION_TYPES.SET_CURRENT_LOCATION:
      return {
        ...state,
        location: {
          ...state.location,
          current: action.payload,
          isLoading: false,
          error: null
        }
      };
      
    case ACTION_TYPES.SET_LOCATION_LOADING:
      return {
        ...state,
        location: {
          ...state.location,
          isLoading: action.payload,
          error: null
        }
      };
      
    case ACTION_TYPES.SET_LOCATION_ERROR:
      return {
        ...state,
        location: {
          ...state.location,
          isLoading: false,
          error: action.payload
        }
      };
    
    // Preference actions
    case ACTION_TYPES.SET_THEME:
      return {
        ...state,
        preferences: {
          ...state.preferences,
          theme: action.payload
        }
      };
      
    case ACTION_TYPES.SET_DISTANCE_UNIT:
      return {
        ...state,
        preferences: {
          ...state.preferences,
          distanceUnit: action.payload
        }
      };
      
    default:
      return state;
  }
}

// Create the contexts
const AppStateContext = createContext(null);
const AppDispatchContext = createContext(null);

/**
 * Custom hook to access the application state
 * @returns {Object} Application state
 */
export function useAppState() {
  const context = useContext(AppStateContext);
  if (context === undefined) {
    throw new Error('useAppState must be used within an AppProvider');
  }
  return context;
}

/**
 * Custom hook to access the dispatch function
 * @returns {Function} Dispatch function
 */
export function useAppDispatch() {
  const context = useContext(AppDispatchContext);
  if (context === undefined) {
    throw new Error('useAppDispatch must be used within an AppProvider');
  }
  return context;
}

/**
 * App Provider Component
 * 
 * Provides global application state and dispatch function to all child components
 * using React Context API.
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 */
export function AppProvider({ children }) {
  const [state, dispatch] = useReducer(appReducer, initialState);
  
  // Memoize the state to prevent unnecessary rerenders
  const memoizedState = useMemo(() => state, [state]);
  
  return (
    <AppStateContext.Provider value={memoizedState}>
      <AppDispatchContext.Provider value={dispatch}>
        {children}
      </AppDispatchContext.Provider>
    </AppStateContext.Provider>
  );
}

// PropTypes validation
AppProvider.propTypes = {
  children: PropTypes.node.isRequired
};

/**
 * Custom hook that combines state and dispatch in one object
 * @returns {Object} Object containing state and dispatch
 */
export function useApp() {
  return {
    state: useAppState(),
    dispatch: useAppDispatch()
  };
} 